import { useCallback, useEffect, useState } from 'react';
import { adminHandleReport, adminReports } from '@suangua/shared';
import {
  EmptyRow,
  LoadingRow,
  ModerationChoice,
  ModerationModal,
  PageHeader,
  Pagination,
  StatusBadge,
  TableCard,
  useAdminGuard,
  useFromNow,
} from '../parts';

/** 举报列表项 */
type ReportRow = Awaited<ReturnType<typeof adminReports>>['items'][number];
const PAGE_SIZE = 20;

const STATUS_TABS = [
  ['pending', '待处理'],
  ['resolved', '已处理'],
  ['dismissed', '已驳回'],
] as const;

/** 需要弹窗选择期限的处置动作（0006：永久 / 预设 / 自定义天数） */
const TERM_ACTIONS = ['mute_author', 'ban_author', 'ban_avatar'] as const;
type TermAction = (typeof TERM_ACTIONS)[number];

/** 期限动作的弹窗配置 */
const TERM_META: Record<TermAction, { title: string; desc: string; label: string }> = {
  mute_author: { title: '禁言作者', desc: '禁言期间同时禁止发帖与回复，到期自动解禁。', label: '禁言作者' },
  ban_author: { title: '封禁作者', desc: '封禁后立即踢线并禁止登录，到期自动解封。', label: '封禁作者' },
  ban_avatar: { title: '禁止更换头像', desc: '禁止期内无法设置新头像，到期自动恢复。', label: '禁止换头像' },
};

/**
 * 管理端 · 举报处理：状态 Tab + 分页。
 * 处置动作（均置为已处理并下发站内通知）：
 * - 帖子举报：隐藏帖子 / 设为只读 / 禁言作者 / 封禁作者 / 删帖
 * - 评论举报：删除评论 / 禁言作者 / 封禁作者
 * - 头像举报：移除头像 / 禁止更换头像
 * - 期限类动作（禁言/封禁/禁换头像）点击后弹窗选择：永久 / 预设 / 自定义天数
 * - 通用：标记已处理（无处置）/ 驳回（未违规）
 */
export function ReportsPage() {
  const guard = useAdminGuard();
  const fromNow = useFromNow();
  const [items, setItems] = useState<ReportRow[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [status, setStatus] = useState<'pending' | 'resolved' | 'dismissed'>('pending');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [tip, setTip] = useState('');
  /** 期限动作弹窗目标 */
  const [termTarget, setTermTarget] = useState<{ r: ReportRow; action: TermAction } | null>(null);

  const load = useCallback(
    async (p = page) => {
      setLoading(true);
      setError('');
      try {
        const res = await adminReports(status, p, PAGE_SIZE);
        setItems(res.items);
        setTotal(res.total);
        setPage(res.page);
      } catch (e) {
        setError(guard(e));
      } finally {
        setLoading(false);
      }
    },
    [guard, status, page],
  );

  // 状态 Tab 切换 → 回第一页
  useEffect(() => {
    void load(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status]);

  /** 执行处置：期限类动作先弹窗选择期限，其余直接执行（confirmMsg 为高危二次确认文案） */
  async function handle(r: ReportRow, action: string, confirmMsg?: string, choice?: ModerationChoice) {
    if ((TERM_ACTIONS as readonly string[]).includes(action) && !choice) {
      setTermTarget({ r, action: action as TermAction });
      return;
    }
    if (confirmMsg && !window.confirm(confirmMsg)) return;
    setError('');
    setTip('');
    try {
      await adminHandleReport(r.id, action, choice?.days, choice?.permanent);
      setTip(`举报 #${r.id} 已处理${action === 'dismiss' ? '（驳回）' : ''}，结果将通过站内消息通知双方`);
      await load(page);
    } catch (e) {
      setError(guard(e));
    }
  }

  /** 动作按钮通用样式 */
  const btn = 'whitespace-nowrap rounded-md border px-2 py-1 transition-colors';
  const btnAmber = `${btn} border-amber-200 text-amber-600 hover:bg-amber-50`;
  const btnRed = `${btn} border-red-200 text-red-500 hover:bg-red-50`;
  const btnGray = `${btn} border-stone-200 text-stone-500 hover:bg-stone-100`;

  return (
    <div>
      <PageHeader title="举报处理" desc="删帖 / 删评论为级联操作；期限类处置弹窗选择永久或天数，结果通过站内消息通知双方">
        {/* 状态 Tab */}
        <div className="flex overflow-hidden rounded-lg border border-stone-200 text-xs">
          {STATUS_TABS.map(([key, label]) => (
            <button
              key={key}
              onClick={() => setStatus(key)}
              className={`px-3 py-1.5 transition-colors ${
                status === key ? 'bg-stone-800 font-medium text-white' : 'bg-white text-stone-500 hover:bg-stone-50'
              }`}
            >
              {label}
            </button>
          ))}
        </div>
      </PageHeader>

      {error && <p className="mb-3 rounded-lg border border-red-200 bg-red-50 px-4 py-2 text-sm text-red-600">{error}</p>}
      {tip && <p className="mb-3 rounded-lg border border-green-200 bg-green-50 px-4 py-2 text-sm text-green-700">{tip}</p>}

      <TableCard footer={<Pagination page={page} size={PAGE_SIZE} total={total} onPage={(p) => void load(p)} />}>
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-stone-100 text-left text-xs text-stone-400">
              <th className="px-4 py-2.5 font-medium">举报对象</th>
              <th className="px-4 py-2.5 font-medium">原因 / 举报人</th>
              <th className="px-4 py-2.5 font-medium">状态</th>
              <th className="px-4 py-2.5 font-medium">时间</th>
              <th className="px-4 py-2.5 text-right font-medium">操作</th>
            </tr>
          </thead>
          <tbody>
            {loading && items.length === 0 ? (
              <tr>
                <td colSpan={5}>
                  <LoadingRow />
                </td>
              </tr>
            ) : items.length === 0 ? (
              <tr>
                <td colSpan={5}>
                  <EmptyRow text="暂无相关举报" />
                </td>
              </tr>
            ) : (
              items.map((r) => {
                // 被举报对象与作者
                const isAvatar = r.target_type === 'avatar';
                const subject = isAvatar
                  ? `头像 · ${r.target_username ?? `用户 #${r.target_user_id ?? '?'}`}`
                  : r.post_title
                    ? `帖子「${r.post_title}」`
                    : r.comment_content
                      ? `评论「${r.comment_content.slice(0, 40)}」`
                      : '（对象已删除）';
                const authorName = isAvatar
                  ? r.target_username
                  : r.post_id
                    ? r.post_author
                    : r.comment_author;
                return (
                  <tr key={r.id} className="border-b border-stone-50 align-top last:border-0 hover:bg-stone-50/60">
                    <td className="max-w-0 px-4 py-2.5">
                      <p className="truncate text-stone-700">{subject}</p>
                      <p className="text-xs text-stone-400">
                        #{r.id} · {isAvatar ? '头像举报' : '内容举报'}
                        {authorName ? ` · 作者：${authorName}` : ''}
                      </p>
                    </td>
                    <td className="max-w-0 px-4 py-2.5">
                      <p className="truncate text-xs text-stone-600">{r.reason}</p>
                      <p className="text-xs text-stone-400">by {r.reporter}</p>
                    </td>
                    <td className="px-4 py-2.5">
                      {r.status === 'pending' ? (
                        <StatusBadge tone="amber">待处理</StatusBadge>
                      ) : r.status === 'resolved' ? (
                        <StatusBadge tone="green">已处理{r.handled_by ? ` · ${r.handled_by}` : ''}</StatusBadge>
                      ) : (
                        <StatusBadge tone="gray">已驳回</StatusBadge>
                      )}
                    </td>
                    <td className="whitespace-nowrap px-4 py-2.5 text-xs text-stone-400">{fromNow(r.created_at)}</td>
                    <td className="px-4 py-2.5 text-right text-xs">
                      {r.status !== 'pending' ? (
                        <span className="text-stone-300">—</span>
                      ) : (
                        <div className="flex flex-wrap justify-end gap-1.5">
                          {/* 帖子举报处置 */}
                          {r.post_id && (
                            <>
                              <button className={btnAmber} onClick={() => void handle(r, 'hide_post')}>
                                隐藏帖子
                              </button>
                              <button className={btnAmber} onClick={() => void handle(r, 'lock_post')}>
                                设为只读
                              </button>
                              <button className={btnAmber} onClick={() => void handle(r, 'mute_author')}>
                                禁言作者
                              </button>
                              <button
                                className={btnRed}
                                onClick={() => void handle(r, 'ban_author', `确认封禁作者「${r.post_author ?? '?'}」？其会话将被注销。`)}
                              >
                                封禁作者
                              </button>
                              <button
                                className={btnRed}
                                onClick={() => void handle(r, 'delete_post', '确认删除该帖子？评论与点赞将一并删除，不可恢复。')}
                              >
                                删帖
                              </button>
                            </>
                          )}
                          {/* 评论举报处置 */}
                          {r.comment_id && (
                            <>
                              <button
                                className={btnRed}
                                onClick={() => void handle(r, 'delete_comment', '确认删除该评论？其楼中楼子回复将一并删除。')}
                              >
                                删评论
                              </button>
                              <button className={btnAmber} onClick={() => void handle(r, 'mute_author')}>
                                禁言作者
                              </button>
                              <button
                                className={btnRed}
                                onClick={() => void handle(r, 'ban_author', `确认封禁评论作者「${r.comment_author ?? '?'}」？`)}
                              >
                                封禁作者
                              </button>
                            </>
                          )}
                          {/* 头像举报处置 */}
                          {isAvatar && (
                            <>
                              <button className={btnAmber} onClick={() => void handle(r, 'delete_avatar')}>
                                移除头像
                              </button>
                              <button className={btnAmber} onClick={() => void handle(r, 'ban_avatar')}>
                                禁止换头像
                              </button>
                            </>
                          )}
                          {/* 通用 */}
                          <button className={btnGray} onClick={() => void handle(r, 'resolve')}>
                            已处理
                          </button>
                          <button className={btnGray} onClick={() => void handle(r, 'dismiss')}>
                            驳回
                          </button>
                        </div>
                      )}
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </TableCard>

      {/* 期限类处置弹窗（永久 / 预设 / 自定义天数） */}
      {termTarget && (
        <ModerationModal
          title={`${TERM_META[termTarget.action].title}「${termTarget.r.post_author ?? termTarget.r.comment_author ?? termTarget.r.target_username ?? '?'}」`}
          desc={TERM_META[termTarget.action].desc}
          onConfirm={(choice) => {
            const { r, action } = termTarget;
            setTermTarget(null);
            // 封禁作者高危，保留二次确认（choice 透传避免再次触发弹窗）
            void handle(r, action, action === 'ban_author' ? '确认执行封禁？' : undefined, choice);
          }}
          onClose={() => setTermTarget(null)}
        />
      )}
    </div>
  );
}
